import React, { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useStoreState } from "ezStore";

import { Button, Card, FormGroup } from "@cofano/cck";
import { Grid2 as Grid, Tab, Tabs } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import useTabs from "components/hooks/useTabs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "utils";

import ExpectedCargosForm from "view/cargos/components/createEdit/ExpectedCargosForm";
import LocationSelect from "view/locations/components/selects/LocationSelect";

import { VoyageCallFormTabs } from "view/voyages/utils/VoyageUtils";
import { VoyageSchemaInput, VoyageSchemaOutput } from "view/voyages/utils/voyage.schema";

import VoyageCallsTabsForm from "./VoyageCallsTabsForm";
import VoyageCallTimesFormFields from "./VoyageCallTimesFormFields";

import { ChildProps } from "types/commons";
import { GetVoyageQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";

import "./styling.scss";

interface VoyageCallsFormProps {
	voyage: ChildProps<GetVoyageQuery, "voyage"> | null
}

const VoyageCallsForm = ({ voyage }: VoyageCallsFormProps) => {

	const { control, formState, watch } = useFormContext<VoyageSchemaInput, unknown, VoyageSchemaOutput>();

	const values = watch();

	const session = useStoreState(store => store.session);

	const { isRTR } = usePermission();

	const hasExpectedCargosPermission = hasPluginPermission(session, "useContainers") &&
		hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos") &&
		!values.isBallast;

	const { selectedTab, selectTab } = useTabs<VoyageCallFormTabs>({
		defaultSelected: VoyageCallFormTabs.Details,
		disableParams: false,
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "calls",
	});

	const [ selectedCallIndex, selectCallIndex ] = useState(0);

	const handleOnDelete = (index: number) => {
		remove(index);

		if (selectedCallIndex !== 0) {
			selectCallIndex(selectedCallIndex - 1);
		}
	};

	const currentCall = voyage?.calls?.[selectedCallIndex];
	const isPlannedCargoCall = (currentCall?.nrDischargingCargos ?? 0) >= 1 || (currentCall?.nrLoadingCargos ?? 0) >= 1;

	return (
		<>
			<VoyageCallsTabsForm
				calls={values.calls}
				selectedIndex={selectedCallIndex}
				onSelect={(index) => selectCallIndex(index)}
				errors={formState.errors} />

			<div style={{ display: "flex", justifyContent: "end", padding: "4px 0 16px" }}>
				<Button
					onClick={() => {
						append({
							id: null,
							location: null,
							atn: null,
							dtn: null,
							ata: null,
							atd: null,
							ateo: null,
							atso: null,
							eta: null,
							etd: null,
							eteo: null,
							etso: null,
							pta: null,
							ptd: null,
							expectedCargos: [],
						});
					}}
					variant="outlined"
					size="small">
					{Language("CALL_ADD")}
				</Button>
			</div>

			{fields.length > 0 && (
				<div className="voyage-calls-create-edit-block">
					<Card
						title={(
							<Tabs variant='scrollable' value={selectedTab} onChange={(_event, newValue) => selectTab(newValue)}>
								<Tab
									label={Language("DETAILS")}
									value={VoyageCallFormTabs.Details} />

								{hasExpectedCargosPermission && (
									<Tab
										label={Language("EXPECTED_CONTAINERS")}
										value={VoyageCallFormTabs.Expected} />
								)}
							</Tabs>
						)}
						actions={[ {
							icon: <DeleteIcon />,
							onClick: () => handleOnDelete(selectedCallIndex),
							hidden: isPlannedCargoCall,
						} ]}
						disablePadding
					/>
					{selectedTab === VoyageCallFormTabs.Details && (
						<Card>
							<Grid spacing={3} container style={{ marginBottom: 16 }}>
								<Grid size={{ xs: 12, lg: 6, xl: 3 }}>
									<FormGroup label={Language("LOCATION")} overflow="hidden" required>
										<Controller
											name={`calls.${selectedCallIndex}.location`}
											control={control}
											render={({ field }) => (
												<React.Fragment>
													<LocationSelect {...field} disabled={!isRTR} clearable />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									</FormGroup>
								</Grid>
							</Grid>

							<Grid size={12}>
								<VoyageCallTimesFormFields control={control} index={selectedCallIndex} />
							</Grid>
						</Card>
					)}

					{selectedTab === VoyageCallFormTabs.Expected && hasExpectedCargosPermission && (
						<ExpectedCargosForm
							key={selectedCallIndex}
							control={control}
							formState={formState}
							nameBase={`calls.${selectedCallIndex}.expectedCargos`}
							expectedCargos={values.calls[selectedCallIndex].expectedCargos ?? []} />
					)}
				</div>
			)}
		</>
	);
};

export default VoyageCallsForm;
